import React, { useContext } from 'react';
import { AuthContext } from '../../context/AuthProvider';
import GoogleAuthBoundary from '../../social/GoogleAuthBoundary';

const AuthModalView = React.lazy(() => import('./AuthModal'));

function ConnectedAuthModal(props: { open: boolean; onClose: () => void }) {
  const auth = useContext(AuthContext);
  const modal = <AuthModalView {...props} auth={auth || undefined} />;
  if (!auth) return modal;
  return <GoogleAuthBoundary>{modal}</GoogleAuthBoundary>;
}

type OpenAuthModalOptions = {
  onClose?: () => void;
};

type Controller = {
  open: (options?: OpenAuthModalOptions) => void;
  close: () => void;
};

let controller: Controller | null = null;

/** Open the login dialog from a header, a page, or any click handler. */
export function openAuthModal(options?: OpenAuthModalOptions) {
  controller?.open(options);
}

/** Close the login dialog from anywhere it was opened. */
export function closeAuthModal() {
  controller?.close();
}

type AuthModalProviderProps = {
  children?: React.ReactNode;
};

type AuthModalProviderState = {
  open: boolean;
  /** Set on the first open and kept, so the dialog can animate out and host email verification. */
  mounted: boolean;
};

/**
 * Host for the login dialog. Mount once near the app root.
 * Other screens call `openAuthModal` and `closeAuthModal`.
 */
export class AuthModalProvider extends React.Component<AuthModalProviderProps, AuthModalProviderState> {
  state: AuthModalProviderState = { open: false, mounted: false };

  private onDismiss: (() => void) | null = null;

  componentDidMount() {
    controller = { open: this.open, close: this.close };
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('authModal') === '1') {
      this.open();
    }
  }

  componentWillUnmount() {
    if (controller?.open === this.open) {
      controller = null;
    }
  }

  open = (options?: OpenAuthModalOptions) => {
    this.onDismiss = options && options.onClose ? options.onClose : null;
    this.setState({ open: true, mounted: true });
  };

  close = () => {
    const dismiss = this.onDismiss;
    this.onDismiss = null;
    this.setState({ open: false });
    if (dismiss) dismiss();
  };

  render() {
    return (
      <>
        {this.props.children}
        {this.state.mounted ? (
          <React.Suspense fallback={null}>
            <ConnectedAuthModal open={this.state.open} onClose={this.close} />
          </React.Suspense>
        ) : null}
      </>
    );
  }
}
